// Rainbow music notes rising from an instrument's keys while its melody plays.
// Rainbow so they read on any wall or floor; a soft dark shadow under each note
// keeps them visible even over white furniture.
import * as PIXI from 'pixi.js';
import { MUSICAL_INSTRUMENT_KEYS } from '../../../data/furniture';

const NOTES_PER_SECOND = 3.2;
const LIFE_S = 2.6;
const RISE_PX_PER_S = 46; // room px
const DRIFT_PX = 14; // side-to-side sway, room px
const HUE_DEG_PER_S = 120; // each note keeps cycling through the rainbow
const LAYER_Z = 999990; // above furniture, below the editor overlays

/** One eighth note ♪ and a beamed pair ♫, drawn white so a tint colours them. */
function noteShapes(): PIXI.GraphicsContext[] {
  const eighth = new PIXI.GraphicsContext()
    .ellipse(0, 0, 6, 4.4).fill(0xffffff)
    .rect(4.4, -19, 2, 19).fill(0xffffff)
    .moveTo(6.4, -19).bezierCurveTo(13, -15, 14, -10, 10, -5).bezierCurveTo(11, -10, 9, -13, 6.4, -14).fill(0xffffff);
  const beamed = new PIXI.GraphicsContext()
    .ellipse(0, 0, 5.4, 4).fill(0xffffff)
    .ellipse(14, -3, 5.4, 4).fill(0xffffff)
    .rect(4, -19, 2, 19).fill(0xffffff)
    .rect(18, -22, 2, 19).fill(0xffffff)
    .poly([4, -19, 20, -22, 20, -17, 4, -14]).fill(0xffffff);
  return [eighth, beamed];
}

function hueToColor(hue: number): number {
  return new PIXI.Color({ h: ((hue % 360) + 360) % 360, s: 95, l: 62 }).toNumber();
}

interface Note {
  view: PIXI.Container;
  ink: PIXI.Graphics;
  x: number;
  y: number;
  age: number;
  hue: number;
  phase: number;
}

export interface MusicNotes {
  /** Start notes from this instrument (replaces the previous one). */
  start: (sprite: PIXI.Sprite, catalogId: number) => void;
  /** Stop emitting; notes already in the air finish their flight. */
  stop: () => void;
  destroy: () => void;
}

export function createMusicNotes(room: PIXI.Container, ticker: PIXI.Ticker): MusicNotes {
  const layer = new PIXI.Container();
  layer.zIndex = LAYER_Z;
  layer.eventMode = 'none';
  room.addChild(layer);
  const shapes = noteShapes();
  const notes: Note[] = [];
  let source: { sprite: PIXI.Sprite; keys: readonly [number, number, number, number] } | null = null;
  let carry = 0;
  let running = false;

  /** A random point on the instrument's keyboard, in room coordinates. */
  const keyPoint = (): { x: number; y: number } | null => {
    if (!source || source.sprite.destroyed || !source.sprite.parent) return null;
    const b = source.sprite.getBounds();
    const [x0, y0, x1, y1] = source.keys;
    const t = Math.random();
    let fx = x0 + (x1 - x0) * t;
    const fy = y0 + (y1 - y0) * t;
    if (source.sprite.scale.x < 0) fx = 1 - fx; // the art is mirrored on the other wall
    return room.toLocal({ x: b.x + b.width * fx, y: b.y + b.height * fy });
  };

  const emit = () => {
    const p = keyPoint();
    if (!p) return;
    const shape = shapes[Math.random() < 0.6 ? 0 : 1];
    const shadow = new PIXI.Graphics(shape);
    shadow.tint = 0x000000;
    shadow.alpha = 0.35;
    shadow.position.set(1.5, 1.5);
    shadow.scale.set(1.12);
    const ink = new PIXI.Graphics(shape);
    const view = new PIXI.Container();
    view.addChild(shadow, ink);
    view.position.set(p.x, p.y);
    view.alpha = 0;
    layer.addChild(view);
    notes.push({ view, ink, x: p.x, y: p.y, age: 0, hue: Math.random() * 360, phase: Math.random() * Math.PI * 2 });
  };

  const update = (tk: PIXI.Ticker) => {
    const dt = Math.min(tk.deltaMS, 100) / 1000;
    if (source) {
      carry += dt * NOTES_PER_SECOND;
      while (carry >= 1) { carry -= 1; emit(); }
    }
    for (let i = notes.length - 1; i >= 0; i--) {
      const n = notes[i];
      n.age += dt;
      const k = n.age / LIFE_S;
      if (k >= 1) {
        n.view.destroy({ children: true });
        notes.splice(i, 1);
        continue;
      }
      n.view.position.set(n.x + Math.sin(n.phase + n.age * 2.4) * DRIFT_PX, n.y - n.age * RISE_PX_PER_S);
      n.view.alpha = k < 0.15 ? k / 0.15 : 1 - Math.max(0, (k - 0.6) / 0.4);
      n.view.scale.set(0.85 + 0.5 * Math.min(1, k * 2));
      n.view.rotation = Math.sin(n.phase + n.age * 3) * 0.18;
      n.ink.tint = hueToColor(n.hue + n.age * HUE_DEG_PER_S);
    }
    if (!source && notes.length === 0 && running) {
      ticker.remove(update);
      running = false;
    }
  };

  const ensureRunning = () => {
    if (running) return;
    running = true;
    ticker.add(update);
  };

  return {
    start: (sprite, catalogId) => {
      const keys = MUSICAL_INSTRUMENT_KEYS[catalogId];
      if (!keys) return;
      source = { sprite, keys };
      carry = 1; // the first note right away
      ensureRunning();
    },
    stop: () => {
      source = null;
    },
    destroy: () => {
      source = null;
      if (running) ticker.remove(update);
      running = false;
      notes.length = 0;
      layer.destroy({ children: true });
      shapes.forEach((s) => s.destroy());
    },
  };
}
